import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { UserProgram } from '@/api/entities';
import { getCurrentUser } from '@/lib/supabase';
import {
  trackCampDetailModalOpened,
  trackProgramDetailModalOpened,
  trackProgramEdit,
  trackProgramDelete,
  trackModalClosed,
} from '@/utils/analytics';
import { autoPromoteUserProgramToCommunity } from '@/services/campContributionService';
import { ModalSkeletonLoader } from './modal/ModalSkeletonLoader';
import ProgramHeader from './modal/ProgramHeader';
import QuickFactsBar from './modal/QuickFactsBar';
import PublicInfoSection from './modal/PublicInfoSection';
import PrivateInfoSection from './modal/PrivateInfoSection';
import EditProgramForm from './modal/EditProgramForm';
import FooterActions from './modal/FooterActions';
import ContactProgramModal from './ContactProgramModal';
import { User } from '@/api/entities';

/**
 * Universal Program Modal Component
 * 
 * A unified modal that works in two modes:
 * - 'discovery': For viewing/searching programs from the database
 * - 'user_program': For viewing/editing saved user programs
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp/program data from camp_audit_data
 * @param {string} props.mode - 'discovery' | 'user_program'
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} [props.userProgram] - User program data (required for 'user_program' mode)
 * @param {Function} [props.onSave] - Save handler (required for 'user_program' mode)
 * @param {Function} [props.onDelete] - Delete handler (optional)
 * @param {Object} [props.profile] - User profile data with children (optional)
 * @param {string} [props.error] - Error message to display (optional)
 */
const UniversalProgramModal = ({
  camp,
  mode,
  isOpen,
  onClose,
  userProgram = null,
  onSave = null,
  onDelete = null,
  profile = null,
  onSaveForLater = null,
  error = null,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const editFormRef = useRef(null);
  const modalOpenTimeRef = useRef(null);
  const originalDataRef = useRef(null);
  const { toast } = useToast();

  // Load user data when modal opens
  useEffect(() => {
    const loadUserData = async () => {
      if (isOpen && mode === 'discovery') {
        try {
          const user = await User.me();
          if (user) {
            setCurrentUser({
              id: user.id,
              email: user.email,
              name: user.name || user.full_name,
              phone: user.phone,
            });
            
            // If profile has children, use first child as default (or allow selection)
            if (profile?.children && profile.children.length > 0) {
              setSelectedChild(profile.children[0]);
            } else if (user.children && user.children.length > 0) {
              setSelectedChild(user.children[0]);
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      }
    };
    
    loadUserData();
  }, [isOpen, mode, profile]);

  // Track modal open and close
  useEffect(() => {
    if (isOpen) {
      // Track modal open time
      modalOpenTimeRef.current = Date.now();
      
      // Track modal opened event
      if (mode === 'discovery' && camp) {
        trackCampDetailModalOpened({
          source: 'discovery',
          camp_id: camp.id,
          camp_name: camp.name,
          has_community_data: camp.has_community_enhancements || false,
          community_contribution_count: camp.community_contribution_count || 0,
        });
      } else if (mode === 'user_program' && userProgram) {
        trackProgramDetailModalOpened({
          source: 'my_programs',
          program_id: userProgram.id,
          program_status: userProgram.status,
          has_children: (userProgram.child_names?.length || 0) > 0,
          is_shared: userProgram.promoted_to_audit_id !== null && userProgram.promoted_to_audit_id !== undefined,
        });
        
        // Store original data for change tracking
        originalDataRef.current = {
          status: userProgram.status,
          program_data: userProgram.program_data,
          child_names: userProgram.child_names,
        };
      }
    } else {
      // Track modal close
      if (modalOpenTimeRef.current) {
        const timeOpen = Date.now() - modalOpenTimeRef.current;
        trackModalClosed({
          mode: mode,
          time_open_ms: timeOpen,
          was_edited: isEditMode,
          camp_id: camp?.id || userProgram?.id,
        });
        modalOpenTimeRef.current = null;
      }
      
      // Reset state
      setIsEditMode(false);
      setSaveError(null);
      setIsContactModalOpen(false);
      originalDataRef.current = null;
    }
  }, [isOpen, mode, camp, userProgram, isEditMode]);

  // Prevent navigation away when in edit mode with unsaved changes
  useEffect(() => {
    if (!isEditMode) return;

    function handleBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = '';
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditMode]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isEditMode) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Store the previously focused element
      previousFocusRef.current = document.activeElement;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isEditMode, onClose]);

  // Focus trap - keep focus within modal
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      modalRef.current.addEventListener('keydown', handleTabKey);
      firstElement?.focus();

      return () => {
        modalRef.current?.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isOpen]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleEditMode = () => {
    const enteringEditMode = !isEditMode;
    
    if (enteringEditMode && userProgram) {
      // Track edit started
      trackProgramEdit('program_edit_started', {
        program_id: userProgram.id,
        program_status: userProgram.status,
      });
    }
    
    setIsEditMode((prev) => !prev);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    // Confirm if user made changes
    if (window.confirm('Discard unsaved changes?')) {
      setIsEditMode(false);
      setSaveError(null);
    }
  };

  const handleContactProgram = () => {
    if (!currentUser) {
      toast({
        title: "Sign in required",
        description: "Please sign in to contact programs",
        variant: "destructive",
      });
      return;
    }
    setIsContactModalOpen(true);
  };

  const handleContactModalClose = (result, data) => {
    setIsContactModalOpen(false);
    if (result === 'success') {
      toast({
        title: "Inquiry sent!",
        description: `Your inquiry has been sent to ${data?.programName || 'the program'}. You'll receive their response directly.`,
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!userProgram?.id) {
      setSaveError('No program selected to save');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    const startTime = Date.now();

    try {
      // Validate form
      if (editFormRef.current) {
        const isValid = editFormRef.current.validate();
        if (!isValid) {
          setIsSaving(false);
          return;
        }
      }

      // Get form data
      const updatedData = editFormRef.current?.getData();
      if (!updatedData) {
        throw new Error('Failed to get form data');
      }

      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Calculate changed fields
      const getChangedFields = (original, updated) => {
        const changed = [];
        if (original.status !== updated.status) changed.push('status');
        if (original.child_names?.length !== updated.child_names?.length) changed.push('child_names');
        
        // Compare program_data fields
        const originalData = original.program_data || {};
        const updatedData = updated.program_data || {};
        Object.keys(updatedData).forEach(key => {
          if (originalData[key] !== updatedData[key]) {
            changed.push(`program_data.${key}`);
          }
        });
        
        return changed;
      };

      const changedFields = originalDataRef.current
        ? getChangedFields(originalDataRef.current, updatedData)
        : [];

      // Save to database using UserProgram.update
      await UserProgram.update(userProgram.id, {
        status: updatedData.status,
        program_data: updatedData.program_data,
        child_names: updatedData.child_names,
      });

      const duration = Date.now() - startTime;

      // Track successful update
      trackProgramEdit('program_updated', {
        program_id: userProgram.id,
        program_status: updatedData.status,
        fields_changed: changedFields,
        duration_ms: duration,
        was_shared_after: profile?.share_camp_data_with_community !== false,
      });

      // Re-promote to sync changes to community (fire-and-forget)
      if (user?.id) {
        autoPromoteUserProgramToCommunity(userProgram.id, user.id)
          .then(result => {
            console.log('[Community] Update sync completed:', result);
          })
          .catch(error => {
            console.error('[Community] Update sync failed:', error);
            // Fail silently - don't bother user
          });
      }

      // Update original data ref
      originalDataRef.current = {
        status: updatedData.status,
        program_data: updatedData.program_data,
        child_names: updatedData.child_names,
      };

      // Success - exit edit mode
      setIsEditMode(false);

      // Call parent success handler
      if (onSave) {
        await onSave({
          ...userProgram,
          ...updatedData,
        });
      }

      // Show success message
      toast({
        title: 'Program updated successfully!',
        description: 'Your changes have been saved.',
      });

      // Note: Parent component should refetch or optimistically update
    } catch (error) {
      console.error('Save failed:', error);
      setSaveError(error.message || 'Failed to save changes. Please try again.');
      
      // Track failed update
      trackProgramEdit('program_update_failed', {
        program_id: userProgram.id,
        program_status: userProgram.status,
        error: error.message || 'Unknown error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userProgram) return;

    // Track delete initiated
    trackProgramDelete('program_delete_initiated', {
      program_id: userProgram.id,
      program_status: userProgram.status,
    });

    const confirmed = window.confirm('Are you sure you want to delete this program?');
    
    if (!confirmed) {
      // Track delete cancelled
      trackProgramDelete('program_delete_cancelled', {
        program_id: userProgram.id,
      });
      return;
    }

    try {
      if (onDelete) {
        await onDelete();
        
        // Track successful delete
        trackProgramDelete('program_deleted', {
          program_id: userProgram.id,
          program_status: userProgram.status,
        });
        
        onClose();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      
      // Track failed delete
      trackProgramDelete('program_delete_failed', {
        program_id: userProgram.id,
        error: error.message || 'Unknown error',
      });
    }
  };

  const handleSaveForLater = async () => {
    if (onSaveForLater) {
      await onSaveForLater(camp);
    } else {
      // Fallback implementation
      console.log('Save for later:', camp);
      toast({
        title: "Save for Later",
        description: "Please implement onSaveForLater handler",
        variant: "default",
      });
    }
  };

  // Handle missing or invalid camp data
  if (!camp || !camp.name) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Camp information unavailable</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              We couldn't load the details for this camp. The information may be incomplete or unavailable.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isEditMode && onClose()}>
      <DialogContent
        ref={modalRef}
        className="max-w-4xl w-full max-h-[95vh] p-0 overflow-hidden bg-white rounded-lg shadow-xl flex flex-col"
        hideCloseButton={isEditMode}
        aria-labelledby="program-modal-title"
        aria-describedby="program-modal-description"
      >
        {/* Visually hidden title for accessibility - actual title is in ProgramHeader */}
        <DialogHeader>
          <DialogTitle id="program-modal-title" className="sr-only">
            {camp.name || 'Program Details'}
          </DialogTitle>
        </DialogHeader>
        
        {/* Header with hero image */}
        <ProgramHeader 
          camp={camp} 
          mode={mode}
          userProgram={userProgram}
          onSaveForLater={() => {
            // TODO: Implement save for later functionality
            console.log('Save for later:', camp);
          }}
          onShare={() => {
            // TODO: Implement share functionality
            if (navigator.share) {
              navigator.share({
                title: camp.name,
                text: `Check out ${camp.name} on Summer Squad!`,
                url: window.location.href,
              }).catch(() => {});
            }
          }}
          onGetDirections={() => {
            // TODO: Implement get directions functionality
            if (camp.location) {
              const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(camp.location)}`;
              window.open(mapsUrl, '_blank');
            }
          }}
          onClose={onClose}
        />

        {/* Quick Facts Bar - Sticky */}
        <QuickFactsBar camp={camp} />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 max-h-[calc(95vh-280px)] relative">
          <ScrollArea className="h-full">
            {/* Error Banner (if error prop provided) */}
            {error && !isEditMode && (
              <div className="max-w-4xl mx-auto mb-6">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900 mb-1">Unable to Load Full Details</h4>
                      <p className="text-sm text-red-700">{error}</p>
                      <p className="text-xs text-red-600 mt-2">
                        Some information may be missing or incomplete.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isEditMode ? (
              <>
                {/* Public Information Section */}
                <PublicInfoSection 
                  camp={camp} 
                  error={error}
                  userProgram={mode === 'user_program' ? userProgram : null}
                />

                {/* Private Information Section (only for user_program mode) */}
                {mode === 'user_program' && userProgram && (
                  <PrivateInfoSection
                    userProgram={userProgram}
                    profile={profile}
                    onEdit={handleToggleEditMode}
                  />
                )}
              </>
            ) : (
              <>
                {/* Error Banner */}
                {saveError && (
                  <div className="max-w-4xl mx-auto mb-6">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-900 mb-1">Save Failed</h4>
                          <p className="text-sm text-red-700">{saveError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Form */}
                <EditProgramForm
                  ref={editFormRef}
                  userProgram={userProgram}
                  camp={camp}
                  profile={profile}
                  onSave={handleSaveChanges}
                  onCancel={handleCancelEdit}
                  isSaving={isSaving}
                />
              </>
            )}
          </ScrollArea>

          {/* Loading Overlay */}
          {isSaving && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50 rounded-lg">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-brand-600 mx-auto mb-4 animate-spin" />
                <p className="text-gray-700 font-medium">Saving changes...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <FooterActions
          mode={mode}
          isEditMode={isEditMode}
          camp={camp}
          isSaving={isSaving}
          onSaveForLater={handleSaveForLater}
          onDelete={handleDelete}
          onToggleEditMode={handleToggleEditMode}
          onCancelEdit={handleCancelEdit}
          onSaveChanges={handleSaveChanges}
          onContactProgram={mode === 'discovery' ? handleContactProgram : null}
        />
      </DialogContent>

      {/* Contact Program Modal */}
      {mode === 'discovery' && currentUser && (
        <ContactProgramModal
          isOpen={isContactModalOpen}
          onClose={handleContactModalClose}
          program={camp}
          selectedChild={selectedChild}
          user={currentUser}
        />
      )}
    </Dialog>
  );
};

export default UniversalProgramModal;

